import { Context } from '@nuxt/types'
import authEventBus from '@ustra/nuxt/src/vue/event-bus/global/auth-event-bus'
import { localStorage } from '@ustra/core/src/utils/storage/local-storage'
import { loginService } from '../../services/login-service'
import { LoginResponseAction } from '../../models/login-model'
import * as storageKey from '../../store/modules/storage-key'
import mobileService from '../../services/mobile-service'

/**
 인증 지원 클래스
 
 */

export class Auth {
  private context: Context = null

  // 비밀번호 미변경 사용일 수
  get passwordChangeDays() {
    return this.context.$ustra.hs.store.cmData.appProp.passwordChangeDays
  }

  // 최대 로그인 실패 수
  get maximumLoginFailNumbers() {
    return this.context.$ustra.hs.store.cmData.appProp.maximumLoginFailNumbers
  }

  constructor(context) {
    this.context = context
    // this.listenEventBus()
  }

  private autoLogoutTimer = null
  private listenEventBus() {
    // 인증 실패 처리
    authEventBus.on(async (state) => {
      try {
        if (state.failure) {
          if (state.failureType === 'expired' || state.failureType === 'released') {
            // 이미 인증이 만료된 경우라면 종료
            if (!this.context.$ustra.auth.store.authenticated) {
              await this.logout(false)
              await this.context.$ustra.auth.logout(true, true)
              return
            }

            // ready일 경우는 대기 처리
            this.context.$ustra.nuxt.onReady(async () => await this.processFailure('expired'))
          } else if (state.failureType === 'duplicated') {
            // ready일 경우는 대기 처리
            this.context.$ustra.nuxt.onReady(async () => await this.processFailure('duplicated'))
          }
        }
      } finally {
        if (state.callback) {
          state.callback()
        }
      }
    })
  }

  private async processFailure(type: 'expired' | 'duplicated') {
    // 토큰은 우선 삭제
    await this.context.$ustra.auth.logout(false, true)

    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer)
    }

    // 5 초 뒤에는 로그아웃 처리 수행
    let isExecutedLogout = false
    this.autoLogoutTimer = setTimeout(() => {
      if (isExecutedLogout) {
        return
      }
      isExecutedLogout = true
      this.logout(false)
      this.context.$ustra.auth.logout(true, true)
    }, 5000)

    if (type === 'expired') {
      await alert('인증 정보가 만료되었습니다. 재 인증 후 이용하시기 바랍니다.')
    } else {
      alert('중복 로그인으로 인증 정보가 만료 되었습니다. 재 인증 후 이용하시기 바랍니다.')
    }

    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer)
    }

    if (isExecutedLogout) {
      return
    }
    isExecutedLogout = true

    await this.logout(false)
    await this.context.$ustra.auth.logout(true, true)
  }

  /**
   * 로그인 처리
   */
  async login(userId, userPwd) {
    const loginResult = await loginService.login(userId, userPwd)

    if (!loginResult.data.body) loginResult.data.body = {}

    loginResult.data.body.actionResponse = {}

    return await this.loginProcess(loginResult)
  }

  /**
   * 자동 로그인
   *
   * @returns
   * @memberof Auth
   */
  async autoLogin() {
    const loginToken = this.context.$ustra.hs.store.auth.loginToken

    // logger().debug({
    //   message: '로그인 토큰',
    //   args: [loginToken],
    // })

    if (loginToken) {
      const loginResult = await loginService.autoLogin(loginToken)

      if (!loginResult.data.body) loginResult.data.body = {}

      loginResult.data.body.actionResponse = {}

      return await this.loginProcess(loginResult)
    }
  }

  /**
   * 로그인 후 처리
   *
   * @param {*} loginResult
   * @returns
   * @memberof Auth
   */
  async loginProcess(result) {
    const loginResult = result.data

    // 로그인 토큰 저장
    const loginToken = result.headers['hdhs-ltoken']
    if (loginToken) {
      this.context.$ustra.hs.store.auth.setLoginToken(loginToken)
    }

    if (loginResult.resultCode !== '0000') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: '서버 장애가 발생하였습니다. 관리자에게 문의하시기 바랍니다.',
      }
      return loginResult
    }

    // 성공
    if (loginResult.body.resCd === '0000') {
      // 유저 정보 로드
      await this.context.$ustra.hs.store.auth.loadUserInfo()

      loginResult.body.actionResponse.action = LoginResponseAction.SUCCESS

      // 비밀번호 만료
      if (this.passwordChangeDays && this.passwordChangeDays > 0 && this.passwordChangeDays < loginResult.body.pwdUnchangedDays) {
        // store 반환 정보 적용
        loginResult.body.actionResponse = {
          action: LoginResponseAction.SUCCESS_RECOMMEND_CHANGE_PASSWORD,
          message: `비밀번호 변경 후, ${this.passwordChangeDays}일이 경과하여, 비밀번호 변경이 필요합니다.`,
          nextPath: '/co/chgPw',
        }
        return loginResult
      }

      // 비밀번호 초기화 여부
      if (loginResult.body.resetPassword === 'Y') {
        // store 반환 정보 적용
        loginResult.body.actionResponse = {
          action: LoginResponseAction.SUCCESS_RECOMMEND_CHANGE_PASSWORD,
          message: `패스워드가 초기화 되었습니다. 패스워드 변경 후 로그인 바랍니다.`,
          nextPath: '/co/chgPw',
        }

        return loginResult
      }

      const userId = this.context.$ustra.hs.store.auth.currentUser.userId
      const dvicId = this.context.$ustra.hs.store.cmData.deviceId
      const result = await loginService.isDivcCert(userId, dvicId)
      if (result.msgCd === '0000') {
        // 점유 인증 완료
        loginResult.body.actionResponse.nextPath = this.getNextPath('login2nd')
      } else {
        // 점유 인증 미완료
        loginResult.body.actionResponse.nextPath = '/co/dvicCert'
      }

      return loginResult
    }

    // 사용 불가 사용자
    if (loginResult.body.resCd === 'FS06') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: `시스템을 사용할 수 없는 사용자입니다.`,
      }
      return loginResult
    }

    // 접속 불가 지역
    if (loginResult.body.resCd === 'FS04') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: `허용되지 않은 접속 지역입니다.`,
      }
      return loginResult
    }

    // 휴면 또는 시스템 기간 연장
    if (loginResult.body.resCd === 'FS07') {
      // if (!this.useAuthApproval) {
      //   loginResult.body.actionResponse = {
      //     action: LoginResponseAction.SHOW_DIALOG,
      //     message: `시스템 사용 기간이 만료되었습니다. \n관리자에게 문의하시기 바랍니다.`,
      //   }
      // } else {
      //   loginResult.body.actionResponse = {
      //     action: LoginResponseAction.FAIL_REQUIRE_APPROVAL,
      //     message: `시스템 사용 기간이 만료되었습니다. \n관리자에게 시스템 연장 요청을 진행하시겠습니까?`,
      //     approvalType: '03',
      //     approvalName: '휴면해제 승인 요청',
      //   }
      // }
      return loginResult
    }

    // 비밀번호 만료
    // if (loginResult.body.resCd === 'FS08') {
    //   loginResult.body.actionResponse = {
    //     action: LoginResponseAction.REQUIRE_CHANGE_PASSWORD,
    //     message: `비밀번호 변경 후, 이용 가능합니다.`,
    //     nextPath: '/co/chgPw?pageType=A',
    //   }

    //   return loginResult
    // }

    // 계정 잠김
    if (loginResult.body.resCd === 'FS09') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: `최대 인증 시도를 초과하였습니다. 비밀번호 초기화 또는 1:1 문의를 통해 비밀번호를 초기화해주세요.`,
        approvalType: '01',
        approvalName: '계정 잠김해제 승인 요청',
      }

      return loginResult
    }

    if (loginResult.body.hasUser) {
      if (loginResult.body.loginFailCnt >= this.maximumLoginFailNumbers) {
        loginResult.body.actionResponse = {
          action: LoginResponseAction.SHOW_DIALOG,
          message: `최대 인증 시도를 초과하였습니다. 비밀번호 초기화 또는 1:1 문의를 통해 비밀번호를 초기화해주세요.`,
        }

        return loginResult
      }
    }

    loginResult.body.actionResponse = {
      action: LoginResponseAction.SHOW_DIALOG,
      message: '사용자 아이디 또는 패스워드가 일치하지 않습니다. 비밀번호를 잊으신 경우 비밀번호 초기화를 통해 변경해주세요.(오류횟수 ' + loginResult.body.loginFailCnt + '회)',
    }

    return loginResult
  }

  /**
   * 2차 로그인 처리
   */
  async login2nd(userId: string, authenticationKey: string) {
    const loginResult = await loginService.login2nd(userId, authenticationKey)

    if (!loginResult.data.body) loginResult.data.body = {}

    loginResult.data.body.actionResponse = {}

    return this.login2ndProcess(loginResult)
  }

  async login2ndProcess(result) {
    const loginResult = result.data

    if (loginResult.resultCode === 'FS0F') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: '유효하지 않은 인증 키입니다.',
      }
      return loginResult
    }

    if (loginResult.resultCode !== '0000') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: '서버 장애가 발생하였습니다. 관리자에게 문의하시기 바랍니다.',
      }
      return loginResult
    }

    // 성공
    if (loginResult.body.resCd === '0000') {
      // logger().info({
      //   message: '2차 로그인 성공',
      //   args: [],
      // })

      if (loginResult.body?.menus) {
        await this.context.$ustra.hs.store.menuData.setRawMenus(loginResult.body?.menus.filter((m) => m.sysGbcd === this.context.$ustra.hs.store.cmData.appProp.sysGbcd))
        await this.context.$ustra.hs.store.menuData.setMenus(loginResult.body?.menus.filter((m) => m.sysGbcd === this.context.$ustra.hs.store.cmData.appProp.sysGbcd))
      }

      if (loginResult.body?.myMenus) {
        await this.context.$ustra.hs.store.menuData.setMyMenus(loginResult.body?.myMenus.filter((m) => m.sysGbcd === this.context.$ustra.hs.store.cmData.appProp.sysGbcd))
      }

      //await this.context.$ustra.hs.store.menuData.setRecentMenus(localStorage.getItem(storageKey.RECENT_MENU))

      if (this.context.$ustra.mobile.isNativeRequest) {
        mobileService.tms(
          this.context.$ustra.hs.store.auth.currentUser.userId,
          this.context.$ustra.hs.store.auth.currentUser.venCd,
          this.context.$ustra.hs.store.auth.currentUser.venNm,
          this.context.$ustra.hs.store.auth.currentUser.ven2Cd,
          this.context.$ustra.hs.store.auth.currentUser.ven2Nm,
          localStorage.getItem(storageKey.IS_AUTO_LOGIN)
        )
      }

      loginResult.body.actionResponse.action = LoginResponseAction.SUCCESS
      loginResult.body.actionResponse.nextPath = this.getNextPath('complete')

      return loginResult
    }

    // 점유 미인증
    if (loginResult.body.resCd === 'FS0A') {
      loginResult.body.actionResponse = {
        action: LoginResponseAction.SHOW_DIALOG,
        message: `유효하지 않은 인증 요청입니다.`,
      }
      return loginResult
    }

    return loginResult
  }

  getNextPath(loginType: 'login2nd' | 'complete') {
    // const userMenus = this.context.$ustra.bo.store.mainData.userMenus

    // if (this.context.route.query.rtnUrl) {
    //   return this.context.route.query.rtnUrl as string
    // }

    // if (this.context.$ustra.bo.store.mainData.lastConnectedPath && this.context.$ustra.bo.store.mainData.lastConnectedPath !== '/admin') {
    //   return this.context.$ustra.bo.store.mainData.lastConnectedPath
    // }

    // if (userMenus && userMenus.length > 0) {
    //   const defaultMenu = userMenus.find((um) => um.defMnuYn === 'Y')

    //   if (defaultMenu) {
    //     return defaultMenu.mnuUrl
    //   } else {
    //     return userMenus[0].mnuUrl
    //   }
    // }

    if (loginType === 'login2nd') {
      // 네이티브 생체인증으로 이동
      if (this.context.$ustra.mobile.isNativeRequest) return '/co/login2nd'
      // 웹 SMS/Email 이동
      else return '/co/userCert'
    } else {
      return '/'
    }
  }

  /**
   * 로그아웃 처리
   */
  async logout(redirect = false, remove = false) {
    localStorage.setItem(storageKey.IS_AUTO_LOGIN, false)
    this.context.$ustra.hs.store.auth.setLoginToken(null)
    this.context.$ustra.hs.store.auth.setExtUser(null)
    if (remove) await loginService.logout()
    await this.context.$ustra.auth.logout(redirect)
  }

  /**
   * 비밀번호 유효성을 검증한다.
   * @param usrId
   * @param password
   */
  validatePassword(usrId: string, password: string): void {
    if (password.length < 10 || password.length > 16) {
      throw new Error('비밀번호는 10~16자로 입력하십시오.')
    }

    if (usrId === password) {
      throw new Error('아이디와 비밀번호가 동일합니다.')
    }

    const passwordCntMap: any = {}
    password.split('').forEach((p) => {
      passwordCntMap[p] = passwordCntMap[p] || 0
      passwordCntMap[p] = passwordCntMap[p] + 1
    })

    for (const word in passwordCntMap) {
      if (passwordCntMap[word] > 2) {
        throw new Error('동일 문자를 3자리 이상 사용할 수 없습니다.')
      }
    }

    let nextWordCnt = 0
    let beforeCharCode = null
    for (const word of password.split('')) {
      if (beforeCharCode !== null) {
        if (beforeCharCode + 1 === word.charCodeAt(0)) {
          nextWordCnt++
        } else {
          nextWordCnt = 0
        }
      }

      if (nextWordCnt >= 2) {
        throw new Error('연속된 문자를 3자리 이상 사용할 수 없습니다.')
      }

      beforeCharCode = word.charCodeAt(0)
    }
  }

  /**
   * 메뉴 권한 존재여부 확인
   * @param menuId
   */
  hasMenuAuthority(menuId: string) {
    // const authorities = this.context.$ustra.bo.store.auth.authorities
    // if (!authorities) {
    //   return false
    // }
    // return authorities.some((auth) => auth.menuId === menuId && !auth.functionId)
  }

  /**
   * 지정된 기능 아이디의 권한이 있는지 확인
   * @param fncId 기능 아이디
   */
  hasFunctionAuthority(functionId: string) {
    // const currentMenu = this.context.$ustra.bo.store.cmData.currentProgramMenu
    // // 현재 설정된 메뉴가 없을 경우, true 반환
    // if (!currentMenu) {
    //   return true
    // }
    // const authorities = this.context.$ustra.bo.store.auth.authorities
    // if (!authorities) {
    //   return false
    // }
    // return authorities.some((auth) => auth.menuId === currentMenu.mnuId && auth.functionId === functionId)
  }
}
